import { Observable, Observer } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as  OktaAuth from 'axis-components/components/utilities/lib-okta/okta-auth-js.min';
import { config } from './constants';

@Injectable({
  providedIn: 'root'
})
export class AxisOktaAuthService {

  private loggedInUser: any;
  private _accessToken: any;
  private observer: Observer<boolean>;
  $isAuthenticated: Observable<boolean>;
  oktaAuth;
  constructor(private router: Router) { }

  // invoked after config load
  initOktaAuth() {
    this.oktaAuth = new OktaAuth({
      clientId: config['okta'].clientId,
      issuer: config['okta'].issuer,
      redirectUri: config['okta'].loginRedirectUri,
      postLogoutRedirectUri: config['okta'].logoutRedirectUri,
      pkce: true,
      tokenManager: {
        expireEarlySeconds: (60 * config['okta'].renewTokenBeforeMinutes)
      }
    });
    this.oktaAuth.tokenManager.on('expired', function (key, expiredToken) {
      console.log(`Token with key ${key} has expired!`);
    });
    this.oktaAuth.tokenManager.on('renewed', (key, newToken, oldToken) => {
      console.log(`Token with key ${key} has been renewed`);
    });
    this.oktaAuth.tokenManager.on('error', (err) => {
      console.log(`Okta token error occurred: ${err}`);
      // Okta had error- will clear one token one its own, but we want to clear it all
      // since we are redirecting to get new ones anyway
      this.clearOktaStorage();
      // redirect to login to get new tokens
      this.login(this.router.url);
    });

    this.$isAuthenticated = new Observable((observer: Observer<boolean>) => {
      this.observer = observer;
      this.isAuthenticated().then(val => {
        observer.next(val);
      }).catch(err => {
        console.log('Error in okta isAuthenticated', err);
        localStorage.clear();
        window.location.reload();
      });
    });
  }

  clearOktaStorage() {
    for (const key of ['okta-cache-storage', 'okta-token-storage']) {
      localStorage.removeItem(key);
    }
  }

  async isAuthenticated() {
    // Checks if there is a current accessToken in the TokenManger.
    return !!(await this.oktaAuth.tokenManager.get('accessToken') && await this.oktaAuth.tokenManager.get('idToken'));
  }

  login(originalUrl: string) {
    // Save current URL before redirect
    sessionStorage.setItem('okta-app-url', originalUrl || this.router.url);
    let arrScopes = ['openid', 'email', 'profile', 'gi-apps'];
    if (config.scopes && config.scopes.length > 0) {
      arrScopes = config.scopes;
    }
    // Launches the login redirect.
    // https://developer.okta.com/docs/reference/api/oidc/#access-token-scopes-and-claims
    this.oktaAuth.token.getWithRedirect({
      scopes: arrScopes
    });
  }

  async handleAuthentication() {
    try {
      const res = await this.oktaAuth.token.parseFromUrl();
      this.oktaAuth.tokenManager.add('idToken', res.tokens.idToken);
      this.oktaAuth.tokenManager.add('accessToken', res.tokens.accessToken);
      if (await this.isAuthenticated()) {
        this.getLoggedInUser();
        if (this.observer) {
          this.observer.next(true);
        } else {
          const abc = new Observable((observer: Observer<boolean>) => {
            this.observer = observer;
            observer.next(true);
          });
        }
      }
      // Retrieve the saved URL and navigate back
      const url = sessionStorage.getItem('okta-app-url');
      this.router.navigate([url]);
    } catch (err) {
      console.log('Error in handleAuthentication:', err);
      if (err.errorCode === 'access_denied') {
        this.router.navigate(['401'], { queryParams: { error: err.errorSummary } });
      }
    }
  }

  /**
   * @deprecated
   * Please use oktaLogout() for external users and internalLogout() for internal users.
   */
  async logout() {
    localStorage.clear();
    await this.oktaAuth.revokeAccessToken(this.accessToken);
  }

  /**
   * For external users, On completion, this should automatically redirect to logoutRedirectUri.
   */
  async oktaLogout() {
    localStorage.clear();
    await this.oktaAuth.signOut();
  }

  /**
   * For internal users, Clears the localstorage and revoke AccessToken.
   */
  async internalLogout() {
    localStorage.clear();
    await this.oktaAuth.revokeAccessToken(this.accessToken);
    this._accessToken = await this.oktaAuth.tokenManager.get('accessToken');
  }

  get accessToken() {
    return this._accessToken;
  }

  get clientId() {
    return config['okta'] && config['okta'].clientId ? config['okta'].clientId : '';
  }

  async getLoggedInUser() {
    const accessToken = await this.oktaAuth.tokenManager.get('accessToken');
    if (!accessToken) {
      this.loggedInUser = undefined;
      this._accessToken = undefined;
      return;
    }
    this._accessToken = accessToken;

    const idToken = await this.oktaAuth.tokenManager.get('idToken');
    if (!idToken || !idToken.claims) {
      this.loggedInUser = undefined;
    }

    this.loggedInUser = {
      email: idToken?.claims?.email,
      name: idToken?.claims?.name,
      userId: idToken?.claims?.eid,
      preferred_username: idToken?.claims?.preferred_username,
      accessToken: accessToken?.value,
      accessTokenDetails: accessToken,
      idTokenDetails: idToken
    };
    return this.loggedInUser;
  }

  isURL(strUrl: string) {
    try {
      return new URL(strUrl).toString();
    } catch (e) {
      return false;
    }
  }
}
